import { ChangeDetectorRef, Component, forwardRef, inject, ChangeDetectionStrategy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { first } from "rxjs/operators";
import {
  LocationSelectionDialogComponent
} from "../location-selection-dialog/location-selection-dialog.component";
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn
} from "@angular/forms";
import { LatLngLiteral } from "leaflet";

@Component({
  selector: "asset-location-selection",
  templateUrl: "./asset-location-selection.component.html",
  styleUrls: ["./asset-location-selection.component.css"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AssetLocationSelectionComponent),
      multi: true,
    },
  ],
})
export class AssetLocationSelectionComponent implements ControlValueAccessor {
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);


  protected _value: LatLngLiteral = { lat: null, lng: null };
  disabled = false;


  onChange: (val: LatLngLiteral) => unknown;
  onTouched: () => unknown;

  /** Optional: expose current value to your template */
  get value(): LatLngLiteral {
    return this._value;
  }

  /** CVA: write value from the parent form into the component */
  writeValue(value: LatLngLiteral | null): void {
    this._value = value ?? { lat: null, lng: null };

    this.cdr.markForCheck();
  }

  /** CVA: register change callback */
  registerOnChange(fn: (val: LatLngLiteral) => void): void {
    this.onChange = fn;
  }

  /** CVA: register touched callback */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** CVA: set disabled state */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  private parseValue(val: string | number): number | null {
    const parsed = Number.parseFloat(val.toString());

    if (Number.isNaN(parsed)) {
      return null;
    }

    return parsed;
  }

  updateLatitude(val: string | number): void {
    this._value = { ...this._value, lat: this.parseValue(val) };
    this.onChange(this._value);
  }

  updateLongitude(val: string | number): void {
    this._value = { ...this._value, lng: this.parseValue(val) };
    this.onChange(this._value);
  }

  clearLatitude(): void {
    this._value = { ...this._value, lat: null };
    this.onChange(this._value);
  }

  clearLongitude(): void {
    this._value = { ...this._value, lng: null };
    this.onChange(this._value);
  }

  /** Open dialog and propagate selection back to the form */
  toggleMapDialog(): void {
    if (this.disabled) return;

    const dialogRef = this.dialog.open(LocationSelectionDialogComponent, {
      width: "600px",
      data: {...this._value} as LatLngLiteral,
    });

    dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe((result: LatLngLiteral | undefined) => {
        if (!result) {
          this.onTouched();
          return;
        }
        this._value = result;
        this.onChange(this._value); // notify form
        this.onTouched();           // mark as touched
        this.cdr.markForCheck();
      });
  }
}


export const LocationValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value as LatLngLiteral | null;
  if (!value) return null; // no value = no error, handle separately with required()

  const { lat, lng } = value;
  const latOk = lat === null || !isNaN(lat);
  const lonOk = lng === null || !isNaN(lng);

  return latOk && lonOk ? null : { invalidNumber: true };
};